import { Link, useLocation } from "react-router-dom";
import { alpha, useTheme, styled } from "@mui/material/styles";
import { Box, List, ListItemText, ListItemIcon, Button, Collapse, ListItemButton, Divider, Typography } from "@mui/material";
import { APP_USER_SESSION } from "../constant/AppConstants";
import { StorageUtils } from "../utils";
import { ExpandMore, ExpandLess, OpenInNew } from "@mui/icons-material";
import _ from "lodash";
import { useEffect, useState, useRef } from "react";
import { hasScope } from "../utils/openidUtils";

// -------------------- styled --------------------
const ListItemIconStyle = styled(ListItemIcon)({
  fontSize: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

// -------------------- helper --------------------
const mergeDividers = (items) => {
  const merged = [];
  let lastWasDivider = false;

  items.forEach((item) => {
    if (item.divider) {
      if (!lastWasDivider) {
        merged.push(item);
        lastWasDivider = true;
      }
    } else {
      merged.push(item);
      lastWasDivider = false;
    }
  });

  return merged;
};

const route = (path) => {
  if (!path) return;
  return { component: Link, to: path };
};

// ----------------------------------------------------------------------
function NavItem({ item, active, setActive, userRole, levelMenu, setLevelMenu }) {
  const theme = useTheme();
  const { title, path, icon } = item;
  const { pathname } = useLocation();

  useEffect(() => {
    if (item.path === pathname) {
      setActive(pathname);
    } else if (!_.isEmpty(item.sub)) {
      const traverse = (items) => {
        for (const firstLevel of items) {
          let found = false;
          if (!_.isEmpty(firstLevel.sub)) {
            const hasItem = firstLevel.sub.find((secondLevel) => secondLevel.path === pathname);
            if (hasItem) {
              found = true;
            }
          } else if (firstLevel.path === pathname) {
            found = true;
          }

          if (found) {
            setActive(pathname);
            break;
          }
        }
      };
      traverse(item.sub);
    }
  }, [item, pathname, setActive]);

  const defaultStyle = {
    ...theme.typography.body2,
    height: 40,
    position: "relative",
    textTransform: "capitalize",
    textAlign: "left",
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(2),
    color: theme.palette.text.secondary,
    "&:focus": { outline: "none" },
    "&:before": {
      top: 0,
      right: 0,
      width: 3,
      bottom: 0,
      content: '""',
      display: "none",
      position: "absolute",
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
      backgroundColor: theme.palette.primary.main,
    },
  };

  const activeRootStyle = {
    ...defaultStyle,
    color: "primary.main",
    fontWeight: "fontWeightMedium",
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    "&:before": { display: "block" },
    borderRadius: "unset",
  };

  const handleOpenLevelMenu = (title, pathName, hasSub) => {
    if (pathName && active !== pathName) setActive(pathName);

    if (hasSub) {
      if (!_.includes(levelMenu, title)) setLevelMenu([...levelMenu, title]);
      else setLevelMenu(levelMenu.filter((item) => item !== title));
    }
  };

  const handleFirstLevelClick = (firstLevelItem) => {
    if (firstLevelItem.link) {
      window.open(firstLevelItem.link);
    } else {
      handleOpenLevelMenu(firstLevelItem.title, firstLevelItem.path, !_.isEmpty(firstLevelItem.sub));
    }
  };

  const hasExpandIcon = (title) => (levelMenu.includes(title) ? <ExpandLess /> : <ExpandMore />);

  const menuActive = (pathName) => (active && active === pathName ? { color: "#106ba3", backgroundColor: "#f7f8f9" } : {});

  const firstLevelItems = mergeDividers(item?.sub || []);

  return (
    <Box sx={{ mb: 1 }} component="div" className="navbar-item">
      <Button
        {...route(path)}
        sx={{ ...activeRootStyle, ...menuActive(path) }}
        fullWidth
        onClick={() => handleOpenLevelMenu(title, path, !_.isEmpty(item?.sub))}
        className="icon-parent"
      >
        <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
        <ListItemText primary={title} className="item" />
        <Box component="div" className="item">{!_.isEmpty(item?.sub) && hasExpandIcon(title)}</Box>
      </Button>

      {firstLevelItems.map((firstLevelItem) => {
        if (!hasScope(firstLevelItem.scope, userRole, firstLevelItem.role)) return null;
        if (!firstLevelItem.path && !firstLevelItem.link && !firstLevelItem.divider) return null;

        return (
          <Collapse in={levelMenu.includes(item.title)} timeout="auto" unmountOnExit key={firstLevelItem.title || Math.random()}>
            {firstLevelItem.divider ? (
              <Divider />
            ) : (
              <List component="div" disablePadding>
                <ListItemButton
                  className="icon-btn"
                  {...route(firstLevelItem.path)}
                  sx={{ ...activeRootStyle, pl: 4, backgroundColor: "#f7f8f9", ...menuActive(firstLevelItem.path) }}
                  onClick={() => handleFirstLevelClick(firstLevelItem)}
                >
                  <ListItemIconStyle>{firstLevelItem?.icon}</ListItemIconStyle>
                  <ListItemText primary={firstLevelItem.title} className="item" />
                  <Box component="div" className="item">
                    {firstLevelItem.link && <OpenInNew fontSize="small" />}
                    {!_.isEmpty(firstLevelItem?.sub) && hasExpandIcon(firstLevelItem.title)}
                  </Box>
                </ListItemButton>

                {mergeDividers(firstLevelItem?.sub || []).map((secondLevelItem) => {
                  if (!hasScope(secondLevelItem.scope, userRole, secondLevelItem.role)) return null;

                  return (
                    <Collapse in={levelMenu.includes(firstLevelItem.title)} timeout="auto" unmountOnExit key={secondLevelItem.title || Math.random()}>
                      {secondLevelItem.divider ? (
                        <Divider />
                      ) : (
                        <List component="div" disablePadding>
                          <ListItemButton
                            className="icon-btn"
                            {...route(secondLevelItem.path)}
                            sx={{ ...activeRootStyle, pl: 6, bgcolor: "none", ...menuActive(secondLevelItem.path) }}
                            onClick={() => {
                              setActive(secondLevelItem.path);
                            }}
                          >
                            <ListItemIconStyle>{secondLevelItem?.icon}</ListItemIconStyle>
                            <ListItemText primary={secondLevelItem.title} className="item" />
                          </ListItemButton>
                        </List>
                      )}
                    </Collapse>
                  );
                })}
              </List>
            )}
          </Collapse>
        );
      })}
    </Box>
  );
}

// ----------------------------------------------------------------------
export default function NavSection({ navConfig, ...other }) {
  const userRole = StorageUtils.getParsedValue(APP_USER_SESSION).role;
  const [active, setActive] = useState();
  const [levelMenu, setLevelMenu] = useState([]);

  const allMenuTitlesRef = useRef([]);

  useEffect(() => {
    const allMenuWithSub = [];
    const traverse = (items) => {
      items.forEach((item) => {
        if (!_.isEmpty(item.sub)) {
          allMenuWithSub.push(item.title);
          traverse(item.sub);
        }
      });
    };
    traverse(navConfig);
    setLevelMenu(allMenuWithSub);

    allMenuTitlesRef.current = [];
    const traverseAllTitles = (items) => {
      items.forEach((item) => {
        if (!_.isEmpty(item.sub)) {
          allMenuTitlesRef.current.push(item.title);
          traverseAllTitles(item.sub);
        }
      });
    };
    traverseAllTitles(navConfig);
  }, [navConfig]);

  // ---- Collapse/Expand All ----
  const toggleAllMenus = () => {
    if (levelMenu.length === allMenuTitlesRef.current.length) {
      setLevelMenu([]);
    } else {
      setLevelMenu([...allMenuTitlesRef.current]);
    }
  };

  return (
    <Box {...other}>
      <Button
        fullWidth
        className="collapse-all-btn"
        onClick={toggleAllMenus}
        sx={{
          justifyContent: "flex-start",
          textTransform: "capitalize",
          height: 40,
          pl: 2.5,
          pr: 2,
          color: "text.secondary",
          fontWeight: "fontWeightMedium",
          bgcolor: "transparent",
          borderBottom: "1px solid #919eab3d",
          borderRadius: 0,
          "&:hover": {
            bgcolor: "action.hover",
          },
          "&:focus": { outline: "none" },
        }}
        startIcon={levelMenu.length === allMenuTitlesRef.current.length ? <ExpandLess /> : <ExpandMore />}
      >
        <span>{levelMenu.length === allMenuTitlesRef.current.length ? "Collapse All" : "Expand All"}</span>
      </Button>

      <List disablePadding>
        {navConfig.map((item) => {
          if (!hasScope(item.scope, userRole, item.role)) return null;

          return (
            <NavItem
              key={item.title}
              item={item}
              active={active}
              setActive={setActive}
              userRole={userRole}
              levelMenu={levelMenu}
              setLevelMenu={setLevelMenu}
            />
          );
        })}
      </List>
    </Box>
  );
}